import React from 'react';

type StackGroup = {
  name: string;
  items: string[];
};

const groups: StackGroup[] = [
  {
    name: 'Backend & Services',
    items: ['.NET 8/10 (C#)', 'ASP.NET Core APIs', 'Microservices', 'Event-driven architectures', 'REST & GraphQL'],
  },
  {
    name: 'Cloud & Infrastructure',
    items: ['AWS ECS & Fargate', 'AWS Lambda', 'SQS / SNS', 'DynamoDB', 'S3', 'AppSync', 'ElastiCache', 'CloudWatch'],
  },
  {
    name: 'Frontend & Clients',
    items: ['React', 'TypeScript', 'Tailwind CSS', 'Component libraries', 'Design systems', 'Internal tools'],
  },
  {
    name: 'Data & Storage',
    items: ['PostgreSQL', 'SQL Server', 'Azure Cosmos DB', 'ETL pipelines', 'Streaming data', 'Analytics integrations'],
  },
  {
    name: 'DevEx & Delivery',
    items: ['GitHub Actions', 'CI/CD pipelines', 'IaC & environment automation', 'Observability & logging'],
  },
];

const TechStack: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-300">
        Tech stack
        <span className="h-1 w-1 rounded-full bg-cyan-400" />
      </div>
      <p className="max-w-2xl text-sm text-slate-300">
        I work across the full stack—from low-latency services and cloud infrastructure to the React interfaces that
        teams use every day. Here&apos;s a snapshot of the tools and platforms I&apos;m most fluent in.
      </p>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => (
          <div
            key={group.name}
            className="group rounded-2xl border border-slate-800/80 bg-slate-900/60 p-4 text-xs text-slate-200 shadow-sm transition hover:border-emerald-500/60 hover:bg-slate-900"
          >
            <h3 className="mb-2 text-[13px] font-semibold text-slate-50">{group.name}</h3>
            <ul className="space-y-1">
              {group.items.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-[5px] h-1 w-1 rounded-full bg-slate-500 group-hover:bg-emerald-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-2xl border border-slate-800/80 bg-slate-900/70 p-4 text-xs text-slate-200 shadow-sm">
        <h3 className="mb-2 text-sm font-semibold text-slate-50">This site is built with</h3>
        <p className="mb-3 text-slate-300">
          A lightweight React/TypeScript frontend with Tailwind CSS, wired for Firebase-backed services and deployed via
          standard GitHub workflows.
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#3178c6] text-[11px] font-bold text-white">
              TS
            </span>
            <span className="text-slate-200">TypeScript + React</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#0f172a] text-[11px] font-bold text-[#38bdf8]">
              TW
            </span>
            <span className="text-slate-200">Tailwind CSS</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#ffca28] text-[11px] font-bold text-black">
              F
            </span>
            <span className="text-slate-200">Firebase (services ready)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#181717] text-[11px] font-bold text-white">
              GH
            </span>
            <span className="text-slate-200">GitHub</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#2088ff] text-[11px] font-bold text-white">
              GA
            </span>
            <span className="text-slate-200">GitHub Actions (CI/CD ready)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechStack;

